import { useEffect, useRef, useCallback } from 'react'

/*
  Cancela as requisições em voo quando o componente sai de cena.

  Sem isso, sair de uma tela antes de a resposta voltar deixa a requisição correndo até
  o fim: o aparelho gasta rede e bateria por um dado que ninguém vai ler, e o `catch`
  ainda roda depois, sobre um componente que não existe mais.

  Uso:

    const { getSignal } = useAbortController ()

    const getData = async () => {
      try {
        const { data } = await cutileiApi.get ('/rota', { signal: getSignal () })
        setDados (data)
      } catch (error) {
        if (isCanceled (error)) return
        logApiError (error)
      }
    }

  O `if (isCanceled (error)) return` não é opcional: o axios rejeita a promise ao
  abortar, então sem ele o cancelamento cairia no mesmo caminho de um erro de verdade e
  a tela mostraria mensagem de falha justamente ao ser fechada.

  `abortarPendentes` serve para quem refaz a mesma busca antes de a anterior voltar —
  busca com debounce, filtro que muda rápido — e quer descartar a resposta velha, que
  pode chegar depois da nova e sobrescrever a tela com dado antigo.
*/
export default function useAbortController () {
  const controllerRef = useRef (null)

  const getSignal = useCallback (() => {
    if (!controllerRef.current || controllerRef.current.signal.aborted) {
      controllerRef.current = new AbortController ()
    }

    return controllerRef.current.signal
  }, [])

  const abortarPendentes = useCallback (() => {
    controllerRef.current?.abort ()
    controllerRef.current = new AbortController ()

    return controllerRef.current.signal
  }, [])

  useEffect (() => () => controllerRef.current?.abort (), [])

  return { getSignal, abortarPendentes }
}
